// src/features/auth/components/PasswordDialog.jsx (Updated with error handling)
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Dialpad,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../../../components/common/ProfilePicture';
import PinKeypad from '../../../../../components/common/PinKeypad';

const PIN_LOCKED_CODES = ['PIN__LOCKED', 'PIN__NOT_AVAILABLE', 'SWITCH_SESSION__REQUIRED'];

export default function PasswordDialog({
  open,
  onClose,
  onConfirm,
  onPinConfirm,
  pinAvailable = false,
  loading = false,
  targetUser = null,
  staffMember = null,
  departmentName = '',
  showUserInfo = false,
  serverError = null,
  onClearError = null,
}) {
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Explicit user choice between password and PIN. Defaults to PIN when it's
  // offered (the fast path), but the toggle is always visible so the choice
  // stays explicit rather than automatic.
  const [mode, setMode] = useState(pinAvailable ? 'pin' : 'password');
  const { t } = useTranslation(["auth", "common"]);
  const passwordInputRef = useRef(null);
  const user = targetUser || "unset";

  // Reset to the best default mode whenever a new user is targeted or PIN
  // availability changes (e.g. dialog reopened for a different staff member).
  useEffect(() => {
    setMode(pinAvailable ? 'pin' : 'password');
  }, [pinAvailable, user]);

  // Clear validation error when server error changes
  useEffect(() => {
    if (serverError) {
      setValidationError('');
    }
  }, [serverError]);

  // Focus the password input when in password mode — PIN mode has no input
  // to focus, the keypad captures keystrokes globally instead.
  useEffect(() => {
    if (open && mode === 'password') {
      const timeoutId = setTimeout(() => passwordInputRef.current?.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open, user, mode]);

  const handleClose = () => {
    setPassword('');
    setPin('');
    setShowPassword(false);
    setValidationError('');

    // Clear server error when closing
    if (onClearError) {
      onClearError();
    }

    onClose();
  };

  const handleConfirm = async () => {
    if (loading) return;

    if (mode === 'pin') {
      if (!/^\d{4}$/.test(pin)) {
        setValidationError(t('auth:password_dialog.pin_required'));
        return;
      }

      setValidationError('');
      try {
        await onPinConfirm(pin);
      } catch (error) {
        // Wrong PIN (attempts remaining) or locked out — either way clear the
        // pad so the user isn't stuck staring at 4 already-filled dots.
        setPin('');
        if (PIN_LOCKED_CODES.includes(error?.code)) {
          // Too many PIN attempts (or it just became unavailable) — fall back
          // to password for the rest of this dialog session.
          setMode('password');
        }
      }
      return;
    }

    if (!password.trim()) {
      setValidationError(t('common:messages.password_required'));
      return;
    }

    setValidationError('');
    onConfirm(password);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }

    // Clear server error when user modifies the password
    if (serverError && onClearError) {
      onClearError();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleConfirm();
    }
  };

  const handleModeChange = (_event, newMode) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    setValidationError('');
    if (serverError && onClearError) {
      onClearError();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {mode === 'pin' ? <Dialpad color="primary" /> : <Lock color="primary" />}
          <Box>
            <Typography variant="h6">
              {mode === 'pin' ? t('auth:password_dialog.pin_dialog_title') : t('auth:confirm_password')}
            </Typography>
            {targetUser && (
              <Typography variant="body2" color="text.secondary">
                {showUserInfo 
                  ? t('auth:password_dialog.login_as_user', { user: targetUser })
                  : t('auth:password_dialog.switch_to_user', { user: targetUser })
                }
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {showUserInfo && targetUser && (
          <Box textAlign="center" mb={3}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ProfilePicture
                profilePictureFileId={staffMember?.profile_picture_file_id}
                name={staffMember?.name}
                size={64}
                imageSize="64x64"
                color={staffMember?.color}
              />
            </Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {targetUser}
            </Typography>
            {departmentName && (
              <Typography variant="body2" color="text.secondary">
                {departmentName}
              </Typography>
            )}
          </Box>
        )}

        {/* Display server error if present */}
        {serverError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
          >
            {serverError}
          </Alert>
        )}

        {pinAvailable && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            disabled={loading}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="pin">
              <Dialpad fontSize="small" sx={{ mr: 1 }} />
              {t('auth:password_dialog.use_pin')}
            </ToggleButton>
            <ToggleButton value="password">
              <Lock fontSize="small" sx={{ mr: 1 }} />
              {t('auth:password_dialog.use_password')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>
          {mode === 'pin'
            ? t('auth:password_dialog.enter_pin_login')
            : showUserInfo
              ? t('auth:password_dialog.enter_password_login')
              : t('auth:password_dialog.enter_password_switch')
          }
        </Typography>

        {mode === 'pin' ? (
          <Box sx={{ py: 1 }}>
            <PinKeypad
              value={pin}
              onChange={setPin}
              disabled={loading}
              error={!!validationError}
              autoCapture={mode === 'pin'}
              onComplete={() => handleConfirm()}
              onEnter={handleConfirm}
            />
            {validationError && (
              <Typography variant="body2" color="error" textAlign="center" mt={2}>
                {validationError}
              </Typography>
            )}
          </Box>
        ) : (
          <TextField
            inputRef={passwordInputRef}
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={t('common:labels.password')}
            value={password}
            onChange={handlePasswordChange}
            onKeyPress={handleKeyPress}
            error={!!validationError}
            helperText={validationError}
            disabled={loading}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    disabled={loading}
                    aria-label={showPassword ? t('auth:hide_password') : t('auth:show_password')}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          {t("common:buttons.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || (mode === 'pin' ? pin.length !== 4 : !password.trim())}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {loading 
            ? t('common:messages.logging_in')
            : showUserInfo 
              ? t('common:buttons.login')
              : t('common:buttons.switch_user')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}